import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { addYears, subYears } from 'date-fns';
import { HttpService } from '../../../services/http.service';
import { DataService } from '../../../services/data.service';
import { forkJoin, startWith } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCalendarModule } from 'ng-zorro-antd/calendar';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzFormModule } from 'ng-zorro-antd/form';
import { UserAccountDTO } from '../../../classes/user-account.dto';
import { StorageService } from '../../../services/storage.service';
import { CardListComponent } from '../../../components/card-list/card-list.component';
import { Router } from '@angular/router';
import { ICarouselElement } from '../../../interfaces/carousel-element.interface';
import { CarouselCardComponent } from '../../../components/utility/carousel-card/carousel-card.component';
import { environment } from '../../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { CardNextAppointmentComponent } from '../../../components/card-next-appointment/card-next-appointment.component';
import { VisitOrRefundsComponent } from '../../../components/visit-or-refunds/visit-or-refunds.component';
import { SearchStructureComponent } from '../../../components/search-structure/search-structure.component';
import { APPOINTMENTS_MOCK, FUNCTIONS_MOCK, HEALTBOOKING_TEXT_MOCK } from '../../../mocks/mock-data';
import { GtmService } from '../../../services/gtm.service';

@Component({
  selector: 'app-homepage',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    NzTagModule,
    NzCollapseModule,
    NzModalModule,
    NzCardModule,
    NzIconModule,
    NzListModule,
    NzCalendarModule,
    NzBadgeModule,
    NzGridModule,
    NzFormModule,
    CardListComponent,
    ReactiveFormsModule,
    CarouselCardComponent,
    CardNextAppointmentComponent,
    VisitOrRefundsComponent,
    SearchStructureComponent,
  ],
  templateUrl: './homepage.component.html',
  styleUrl: './homepage.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomepageComponent implements OnInit {
  columns: any;
  appointmentsText: any;
  pastAppointmentsText: any;
  appointments: any[] = [];
  pastAppointments: any = [];
  user: any;
  healthBookingsText: any;
  hasOurServices: boolean = false;
  medicalForm!: FormGroup;
  medicalLimits: any;
  selectedMeasurement: string | null = null;
  measurementOptions = [
    { label: 'Frequenza cardiaca', value: 'bpm' },
    { label: 'Peso corporeo', value: 'weight' },
    { label: 'Temperatura', value: 'temperature' },
    { label: 'Pressione arteriosa', value: 'pressure' },
    { label: 'Glicemia', value: 'glycemia' },
    { label: 'Circonferenza vita', value: 'waistCircumference' },
    { label: 'Circonferenza fianchi', value: 'hipCircumference' },
    { label: 'Circonferenza polsi', value: 'wristCircumference' },
  ];
  measurementTexts: string[] = [];
  measurementValues: string[] = [];
  glycemiaFoodTexts: string[] = [];
  glycemiaFoodValues: string[] = [];
  glycemiaMeasurementTexts: string[] = [];
  glycemiaMeasurementValues: string[] = [];

  glycemiaFoodOptions = [
    { label: 'A digiuno', value: 'D' },
    { label: '2 ore dopo il pasto', value: '2H' },
  ];

  glycemiaMeasurementOptions = [
    { label: 'mg/dl', value: 'MG' },
    { label: 'mmol/l', value: 'MM' },
  ];
  carouselElements?: ICarouselElement;
  functions: string[] = [];
  function?: string;
  baseUrl = environment.baseUrl;
  counters: any[] = [];
  userData: any;
  responseData: any;
  limit?: number;
  endDate: Date = new Date();
  startDate: Date = subYears(this.endDate, 50);
  isDesktop: boolean = true;
  isPreviewMode: boolean = false;
  homeAssistanceUrl: string = '';
  private addedControls: string[] = [];

  constructor(
    private translateService: TranslateService,
    private http: HttpService,
    private dataService: DataService,
    private cdr: ChangeDetectorRef,
    private storage: StorageService,
    private fb: FormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private gtmService: GtmService
  ) {}

  get typeControl(): FormControl {
    return this.medicalForm.get('type') as FormControl;
  }

  ngOnInit(): void {
    this.isPreviewMode = this.router.url.startsWith('/preview-page');
    if (this.isPreviewMode) {
      this.appointments = APPOINTMENTS_MOCK;
      this.healthBookingsText = HEALTBOOKING_TEXT_MOCK;
      this.functions = FUNCTIONS_MOCK;
      this.user = 'DEMO';
      this.initForm();
    } else {
      this.dataService.getHomeAssistanceUrl().subscribe((res) => {
        this.homeAssistanceUrl = res;
      });
      this.getAppointments();
      this.getTexts();
      const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
      this.user = userData.name;
      this.functions = userData.functions;

      this.initForm();
      this.measurementTexts = this.measurementOptions.map((option) => option.label);
      this.measurementValues = this.measurementOptions.map((option) => option.value);
      this.glycemiaFoodTexts = this.glycemiaFoodOptions.map((option) => option.label);
      this.glycemiaFoodValues = this.glycemiaFoodOptions.map((option) => option.value);
      this.glycemiaMeasurementTexts = this.glycemiaMeasurementOptions.map((option) => option.label);
      this.glycemiaMeasurementValues = this.glycemiaMeasurementOptions.map((option) => option.value);
      this.fetchMedicalLimits();

      this.medicalForm.get('type')?.valueChanges.subscribe((value) => {
        this.onMeasurementChange(value);
        this.cdr.detectChanges();
      });
      this.createCarouselElements();
      const individualId = JSON.parse(localStorage.getItem('userData')!)?.individualId;
      if (this.functions.includes('MFD')) {
       /* this.http.getCounters(individualId).subscribe(
          (response) => {
            this.counters = response;
            this.limit = this.counters[0].limit;
          },
          (error) => {
            console.error('Errore nel recupero dei counters:', error);
          }
        );*/
      }
      this.checkScreenSize();
      window.addEventListener('resize', this.checkScreenSize.bind(this));
    }
  }

  initForm(): void {
    this.medicalForm = this.fb.group({
      date: [null, Validators.required],
      type: this.fb.control('', [Validators.required]),
    });
  }
  onMeasurementChange(value: string): void {
    this.selectedMeasurement = value;
    this.handleFormType();
  }

  private handleFormType(): void {
    this.addedControls.forEach((control) => {
      if (this.medicalForm.contains(control)) {
        this.medicalForm.removeControl(control);
      }
    });
    this.addedControls = [];

    switch (this.selectedMeasurement) {
      case 'weight':
        this.medicalForm.addControl(
          'weight',
          new FormControl(null, [Validators.required, Validators.min(this.medicalLimits.weightLimit.min), Validators.max(this.medicalLimits.weightLimit.max)])
        );
        this.addedControls.push('weight');
        break;

      case 'height':
        this.medicalForm.addControl('height', new FormControl(null, Validators.required));
        this.addedControls.push('height');
        break;

      case 'bpm':
        this.medicalForm.addControl(
          'bpm',
          new FormControl(null, [Validators.required, Validators.min(this.medicalLimits.bpmLimits.min), Validators.max(this.medicalLimits.bpmLimits.max)])
        );
        this.addedControls.push('bpm');
        break;

      case 'waistCircumference':
      case 'hipCircumference':
      case 'wristCircumference':
        this.medicalForm.addControl(this.selectedMeasurement, new FormControl(null, Validators.required));
        this.addedControls.push(this.selectedMeasurement);
        break;

      case 'temperature':
        this.medicalForm.addControl(
          'temperature',
          new FormControl(null, [
            Validators.required,
            Validators.min(this.medicalLimits.temperatureLimits.celsius.min),
            Validators.max(this.medicalLimits.temperatureLimits.celsius.max),
          ])
        );
        this.addedControls.push('temperature');
        break;

      case 'isSmoker':
      case 'bloodGroup':
        this.medicalForm.addControl(this.selectedMeasurement, new FormControl(null, Validators.required));
        this.addedControls.push(this.selectedMeasurement);
        this.medicalForm.removeControl('creationDate');
        this.medicalForm.removeControl('creationHour');
        break;

      case 'pressure':
        this.medicalForm.addControl(
          'maxValue',
          new FormControl(null, [
            Validators.required,
            Validators.min(this.medicalLimits.pressureLimits.systolic.min),
            Validators.max(this.medicalLimits.pressureLimits.systolic.max),
          ])
        );
        this.medicalForm.addControl(
          'minValue',
          new FormControl(null, [
            Validators.required,
            Validators.min(this.medicalLimits.pressureLimits.diastolic.min),
            Validators.max(this.medicalLimits.pressureLimits.diastolic.max),
          ])
        );
        this.medicalForm.addControl('pressureBpm', new FormControl(null, [Validators.min(this.medicalLimits.bpmLimits.min), Validators.max(this.medicalLimits.bpmLimits.max)]));
        this.addedControls.push('maxValue', 'minValue', 'pressureBpm');
        break;

      case 'glycemia':
        this.medicalForm.addControl(
          'glycemia',
          new FormControl(null, [Validators.required, Validators.min(this.medicalLimits.glycemiaLimits.min), Validators.max(this.medicalLimits.glycemiaLimits.max)])
        );
        this.medicalForm.addControl('measureType', new FormControl(null, Validators.required));
        this.medicalForm.addControl('measureUnit', new FormControl(null, Validators.required));
        this.addedControls.push('glycemia', 'measureType', 'measureUnit');
        break;
    }
  }

  registerMeasurement(): void {
    const formValues = this.medicalForm.value;
    const individualId = JSON.parse(localStorage.getItem('userData')!)?.individualId;
    const creationDate = this.medicalForm.value.date;
    let requestBody: any = { creationDate, individualId };

    switch (formValues.type) {
      case 'pressure':
        requestBody.bpm = formValues.pressureBpm;
        requestBody.maxValue = formValues.maxValue;
        requestBody.minValue = formValues.minValue;
        break;
      case 'glycemia':
        requestBody.glycemia = formValues.glycemia;
        requestBody.measureType = formValues.measureType;
        requestBody.measureUnit = formValues.measureUnit;
        break;
      case 'temperature':
        requestBody.temperature = formValues.temperature;
        requestBody.measureUnit = 'C';
        break;
      case 'bpm':
        requestBody.bpm = formValues.bpm;
        requestBody.maxValue = 0;
        requestBody.minValue = 0;
        break;
      case 'weight':
        requestBody.weight = formValues.weight;
        break;
      case 'waistCircumference':
        requestBody.waistCircumference = formValues.waistCircumference;
        break;
      case 'hipCircumference':
        requestBody.hipCircumference = formValues.hipCircumference;
        break;
      case 'wristCircumference':
        requestBody.wristCircumference = formValues.wristCircumference;
        break;
    }

    this.addedControls.forEach((control) => {
      if (this.medicalForm.contains(control)) {
        this.medicalForm.removeControl(control);
      }
    });
    this.addedControls = [];

    this.http.updateMedicalStats(formValues.type, requestBody).subscribe((response) => {
      this.toastr.success(this.translateService.instant('toastr.success.savedSuccess'));
      this.medicalForm.reset();
      this.cdr.detectChanges();
    });
  }

  fetchMedicalLimits(): void {
    const individualId = JSON.parse(localStorage.getItem('userData')!)?.individualId;
    this.http.mainmedicalstastlimits(individualId).subscribe(
      (response) => {
        this.medicalLimits = response;
      },
      (error) => {
        console.error('Errore nel recupero dei limiti');
      }
    );
  }

  public getAppointments(): void {
    const individualId = JSON.parse(localStorage.getItem('userData')!).individualId;
    const language = this.translateService.currentLang;
    const $futureAppointments = this.http.getUserStoryActual(individualId, 6, language);
    const $pastAppointments = this.http.getUserStoryPassed(individualId, 6, language);
    forkJoin([$futureAppointments, $pastAppointments]).subscribe(([resFuture, resPast]) => {
      if (resFuture.lista.length > 0) {
        resFuture.lista.map((element: any) => {
          const [date, time] = element.consultDate.split(' ');
          element['time'] = time;
          element.consultDate = date;
          element.statusText = 'statusTable.' + element.status.toLowerCase();
        });
        this.appointments = resFuture.lista;
        this.dataService.setAppointments(this.appointments);
      }
      if (resPast.lista.length > 0) {
        resPast.lista.map((element: any) => {
          const [date, time] = element.consultDate.split(' ');
          element['time'] = time;
          element.consultDate = date;
        });
        this.pastAppointments = resPast.lista;
        this.dataService.setAppointments(this.pastAppointments);
      }
      this.cdr.detectChanges();
    });
  }

  private getTexts() {
    this.translateService.get('medicalConsultations.appointmentsText').subscribe((appointmentsText) => {
      this.appointmentsText = appointmentsText;
    });
    this.translateService.get('medicalConsultations.pastAppointmentsText').subscribe((appointmentsText) => {
      this.pastAppointmentsText = appointmentsText;
    });
    this.translateService.get('ourServices').subscribe((ourServices: any) => {
      if (ourServices && ourServices.servicesList && ourServices.servicesList.length > 0) {
        this.healthBookingsText = ourServices.servicesList;
        this.hasOurServices = true;
      } else {
        this.hasOurServices = false;
      }
    });
    this.translateService.get('appointmentsTable').subscribe((texts) => {
      this.columns = texts.columns;
    });
  }

  protected onCarouselCarePathwayEmit(idCarePathway: any): void {
    this.router.navigate(['/medical-consultations/showcase-discover-care-pathway'], { queryParams: { id: idCarePathway } });
  }

  private createCarouselElements(): void {
    const userData = localStorage.getItem('userData');

    if (userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        this.functions = parsedUserData.functions || [];
      } catch (error) {
        console.error('Errore nel parsing di userData:', error);
      }
    }

    this.translateService.get('medicalConsultations.carouselContent').subscribe((carouselContent) => {
      this.carouselElements = {
        carouselText: {
          title: carouselContent.title,
          subtitle: carouselContent.subtitle,
          middleNumberText: carouselContent.of,
        },
        cardsContent: [],
      };

      if (this.functions.includes('PPP')) {
        this.carouselElements.cardsContent.push({
          id: 'TreatmentPathPsiTelevisitFU',
          image: 'woman-yoga.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[0],
        });
      }

      if (this.functions.includes('PNP')) {
        this.carouselElements.cardsContent.push({
          id: 'TreatmentPathNutTelevisitFU',
          image: 'hand-heart.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[1],
        });
      }

      if (this.functions.includes('PNF')) {
        this.carouselElements.cardsContent.push({
          id: 'TreatmentPathNutTelevisitFU',
          image: 'hand-heart.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[2],
        });
      }

      if (this.functions.includes('PPF')) {
        this.carouselElements.cardsContent.push({
          id: 'TreatmentPathPsiTelevisitFU',
          image: 'woman-yoga.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[3],
        });
      }

      if (this.functions.includes('TSP')) {
        this.carouselElements.cardsContent.push({
          id: 'SpecialistTelevisitVideoPayment',
          image: 'hand-heart.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[4],
        });
      }

      if (this.functions.includes('TGP')) {
        this.carouselElements.cardsContent.push({
          id: 'GenericTelevisitVideoPayment',
          image: 'hand-heart.svg',
          iconButton: 'icon-right-red.svg',
          ...carouselContent.cardsContent[5],
        });
      }
    });
  }

  openPharmap(deliveryType: 'GRATUITA' | 'SCONTATA' | 'PROSEGUI') {
    if(deliveryType === 'GRATUITA' || deliveryType === 'SCONTATA'){
      this.gtmService.pushDeliveryDataEvent(deliveryType);
    }
    const individualId = JSON.parse(localStorage.getItem('userData')!)?.individualId;
    this.http.getAccountdDataNoConnectionValidations(individualId).subscribe((response) => {
      this.userData = response;
      this.getPharmap();
    });
  }

  getPharmap(): void {
    if (!this.userData) return;

    const payload = {
      email: this.userData.customer.email,
      birthDate: this.userData.customer.birthDate,
      name: this.userData.customer.name,
      surname: this.userData.customer.surname,
      cf: this.userData.customer.taxId,
      gender: this.userData.customer.gender,
      phone: this.userData.customer.phone,
      overwrite: false,
      endDate: this.userData.product.expirationDate,
      maxUse: 3,
      idBill: this.userData.product.policyNumber,
      userUid: this.userData.customer.individualId,
    };

    this.http.openPharmap(payload).subscribe(
      (response) => {
        this.responseData = response;
        window.open(response.url, '_blank', 'noopener');
      },
      (error) => {
        console.error(error);
      }
    );
  }

  checkScreenSize() {
    this.isDesktop = window.innerWidth >= 1199;
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.checkScreenSize.bind(this));
  }
}
